import React, { useState, useEffect, useRef } from "react";
import ReactMapGL, {
  Popup,
  NavigationControl,
  FullscreenControl,
  Source,
  Layer,
} from "react-map-gl";
import { Chip, Slider, Paper } from "@material-ui/core";

const MAPBOX_TOKEN = "pk.eyJ1IjoieWFtYXNuYXgiLCJhIjoiY2prOTlub3cwMnQ5dzNsbWxpNXpxdTJoNyJ9.iPwzkcOPb0xHua5IwXCElA";

function PhotoShootMapHokuden({ records, type, width, height, status, seasonId, photoShootMapData }) {
  const popupHovered = useRef(false);
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [mapStyle, setMapStyle] = useState("mapbox://styles/yamasnax/clubgryio000s01p98ef99wcb");
  const [terrainEnabled, setTerrainEnabled] = useState(false);
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [showClusters, setShowClusters] = useState(true);

  const handleMapLoad = () => {
    if (!map && mapRef.current) { // ✅ map が未設定の場合のみ実行
      setMap(mapRef.current.getMap());
    }
  };

  const calculateBounds = (records) => {
    const bounds = records.reduce(
      (acc, curr) => {
        return {
          minLng: Math.min(acc.minLng, parseFloat(curr.longitude)),
          minLat: Math.min(acc.minLat, parseFloat(curr.latitude)),
          maxLng: Math.max(acc.maxLng, parseFloat(curr.longitude)),
          maxLat: Math.max(acc.maxLat, parseFloat(curr.latitude)),
        };
      },
      {
        minLng: Infinity,
        minLat: Infinity,
        maxLng: -Infinity,
        maxLat: -Infinity,
      }
    );

    const paddingPercentage = 0.1;
    const lngPadding = (bounds.maxLng - bounds.minLng) * paddingPercentage;
    const latPadding = (bounds.maxLat - bounds.minLat) * paddingPercentage;

    return {
      minLng: bounds.minLng - lngPadding,
      minLat: bounds.minLat - latPadding,
      maxLng: bounds.maxLng + lngPadding,
      maxLat: bounds.maxLat + latPadding,
    };
  };

  const calculateCenterAndZoom = (bounds) => {
    const center = [
      (bounds.minLng + bounds.maxLng) / 2,
      (bounds.minLat + bounds.maxLat) / 2,
    ];
    const zoom = Math.min(
      Math.log2(960 / (bounds.maxLng - bounds.minLng)),
      Math.log2(480 / (bounds.maxLat - bounds.minLat))
    );
    return { center, zoom };
  };

  const boundsWithPadding = records.reduce(
    (acc, curr) => {
      return {
        minLng: Math.min(acc.minLng, parseFloat(curr.longitude)),
        minLat: Math.min(acc.minLat, parseFloat(curr.latitude)),
        maxLng: Math.max(acc.maxLng, parseFloat(curr.longitude)),
        maxLat: Math.max(acc.maxLat, parseFloat(curr.latitude)),
      };
    },
    {
      minLng: Infinity,
      minLat: Infinity,
      maxLng: -Infinity,
      maxLat: -Infinity,
    }
  );
  const paddingPercentage = 0.1;

  const lngPadding =
    (boundsWithPadding.maxLng - boundsWithPadding.minLng) * paddingPercentage;
  const latPadding =
    (boundsWithPadding.maxLat - boundsWithPadding.minLat) * paddingPercentage;
  const bounds = {
    minLng: boundsWithPadding.minLng - lngPadding,
    minLat: boundsWithPadding.minLat - latPadding,
    maxLng: boundsWithPadding.maxLng + lngPadding,
    maxLat: boundsWithPadding.maxLat + latPadding,
  };

  const center = [
    (bounds.minLng + bounds.maxLng) / 2,
    (bounds.minLat + bounds.maxLat) / 2,
  ];
  const zoom = Math.min(
    Math.log2(960 / (bounds.maxLng - bounds.minLng)),
    Math.log2(480 / (bounds.maxLat - bounds.minLat))
  );

  const [viewport, setViewport] = React.useState({
    "latitude": 43.5,
    "longitude": 142.0,
    "zoom": 6.5,
    bearing: terrainEnabled ? 80 : 0,
    pitch: terrainEnabled ? 80 : 0,
  });

  useEffect(() => {
    if (records.length > 0) {
      const bounds = calculateBounds(records);
      const { center, zoom } = calculateCenterAndZoom(bounds);
      setViewport({
        latitude: center[1],
        longitude: center[0],
        zoom: zoom,
        bearing: terrainEnabled ? 80 : 0,
        pitch: terrainEnabled ? 80 : 0,
      });
    } else {
      setViewport({
        "latitude": 43.5,
        "longitude": 142.0,
        "zoom": 6.5,
        "bearing": 0,
        "pitch": 0
      });
    }
  }, [records, terrainEnabled]);

  const toggleMapStyle = () => {
    setMapStyle((prev) =>
      prev === "mapbox://styles/yamasnax/clubgryio000s01p98ef99wcb"
        ? "mapbox://styles/mapbox/satellite-v9"
        : "mapbox://styles/yamasnax/clubgryio000s01p98ef99wcb"
    );
  };

  const toggleTerrain = () => {
    setTerrainEnabled(!terrainEnabled);
  };

  const totalTaken = photoShootMapData?.taken?.length || 0;
  const totalUntaken = photoShootMapData?.untaken?.length || 0;
  const totalCount = totalTaken + totalUntaken;
  const takenPercentage = totalCount > 0 ? (totalTaken / totalCount) * 100 : 0;
  const untakenPercentage = totalCount > 0 ? (totalUntaken / totalCount) * 100 : 0;

  const geojsonData = {
    type: "FeatureCollection",
    features: records.map((record) => ({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [parseFloat(record.longitude), parseFloat(record.latitude)],
      },
      properties: {
        id: record.id,
        power_asset_id: record.power_asset_id,
        asset_number: record.asset_number,
        branch_code: record.branch_code,
        city_name: record.city_name,
        status: record.status,
        takenPercentage: takenPercentage,
        untakenPercentage: untakenPercentage,
        "icon-image": `donut-${record.id}`,
      },
    })),
  };

  const totalPoints = records.length;

  return (
    <div style={{ width: width, height: height }}>
      <ReactMapGL
        ref={mapRef}
        mapboxAccessToken={MAPBOX_TOKEN}
        style={{ width: "100%", height: "90%" }}
        mapStyle={mapStyle}
        onLoad={handleMapLoad}
        {...viewport}
        onMove={(evt) => setViewport(evt.viewState)}
        onMouseMove={(event) => {
          if (popupHovered.current) return;
          const feature = event.features && event.features[0];
          if (feature) {
            setHoveredFeature(feature);
          } else {
            setHoveredFeature(null);
          }
        }}
        interactiveLayerIds={showClusters ? ["unclustered-points"] : ["unclustered-points-off"]}
        terrain={
          terrainEnabled ? { source: "mapbox-dem", exaggeration: 1.5 } : null
        }
      >
        <div style={{ position: "absolute", top: 100, right: 10, zIndex: 1 }}>
          <FullscreenControl />
          <NavigationControl />
          <div style={{ marginTop: 10, width: 30, height: 200, display: "flex", alignItems: "center" }}>
            <Paper
              style={{
                position: "absolute",
                top: "70%",
                right: "0px",
                transform: "translateY(-50%)",
                paddingTop: "16px",
                paddingBottom: "16px",
                borderRadius: "2px",
                boxShadow: "0px 0px 5px rgba(0,0,0,0.3)",
              }}
            >
              <Slider
                orientation="vertical"
                value={viewport.zoom}
                min={5}
                max={15}
                step={0.1}
                onChange={(event, newValue) =>
                  setViewport({ ...viewport, zoom: newValue })
                }
                aria-labelledby="zoom-slider"
                style={{ height: "160px" }}
              />
            </Paper>
          </div>
        </div>
        <Source
          id="clustered-data"
          type="geojson"
          data={geojsonData}
          cluster={true}  // クラスタリングを有効化
          clusterMaxZoom={14}  // クラスタリングが無効化されるズームレベル
          clusterRadius={60}  // クラスタリングの半径（ピクセル）
          generateId={true}
          clusterProperties={{
            "takenCount": ["+", ["case", ["==", ["get", "status"], "taken"], 1, 0]],
            "untakenCount": ["+", ["case", ["==", ["get", "status"], "untaken"], 1, 0]],
            "takenPercentage": [
              "/",
              ["get", "takenCount"],
              ["+", ["get", "takenCount"], ["get", "untakenCount"]]
            ],
            "untakenPercentage": [
              "coalesce",
              [
                "/",
                ["*", ["get", "untakenCount"], 100],
                ["max", 1, ["+", ["get", "takenCount"], ["get", "untakenCount"]]]
              ],
              0
            ]
          }}
        >
        {showClusters && (
          <>
          {status === "both" && (
            <>
              {/* 撮影済みの青色リング */}
              <Layer
                id="clusters-taken"
                type="circle"
                source="clustered-data"
                filter={["has", "point_count"]}
                paint={{
                  "circle-color": "#ffffff", // クラスターの内側は白
                  "circle-radius": [
                    "step",
                    ["get", "point_count"],
                    30,  // クラスター最小サイズ
                    100, 40,
                    750, 50
                  ],
                  "circle-stroke-width": 10, // 枠線の太さ
                  "circle-stroke-color": [
                    "case",
                    ["==", ["get", "untakenCount"], 0], "#4cb992", // ✅ 100%達成（未撮影ゼロ）の場合、グリーン
                    // "#007AFF" // 🔵 それ以外は青
                    "transparent"
                  ],
                  "circle-stroke-opacity": [
                    "case",
                    [">", ["get", "takenPercentage"], 0], 0.8, // 撮影済みデータがある場合、半透明
                    0
                  ]
                }}
              />

              {/* 未撮影の赤色リング（少し大きめ） */}
              <Layer
                id="clusters-untaken"
                type="circle"
                source="clustered-data"
                filter={["has", "point_count"]}
                paint={{
                  "circle-color": "#ffffff", // クラスターの内側は白
                  "circle-radius": [
                    "step",
                    ["get", "point_count"],
                    34,  // クラスター最小サイズ
                    100, 44,
                    750, 54
                  ],
                  // "circle-stroke-width": 6, // 枠線の太さ
                  "circle-stroke-width": [
                    "interpolate",
                    ["linear"],
                    [
                      "/",
                      ["*", ["get", "untakenCount"], 100], // 未撮影数 × 100
                      ["+", ["get", "untakenCount"], ["get", "takenCount"]] // 総数 = 未撮影 + 撮影済み
                    ],
                    1, 3,   // 0% (全て撮影済み) -> 枠線最小 (1)
                    10, 3,  // 10% -> 枠線 2
                    20, 3,  // 20% -> 枠線 3
                    30, 6,  // 30% -> 枠線 4
                    40, 6,  // 40% -> 枠線 5
                    50, 6,  // 50% -> 枠線 6
                    60, 9,  // 60% -> 枠線 7
                    70, 9,  // 70% -> 枠線 8
                    80, 9,  // 80% -> 枠線 9
                    90, 12  // 90%以上未撮影 -> 枠線最大 (10)
                  ],
                  "circle-stroke-color": [
                    "case",
                    ["==", ["get", "untakenCount"], 0], "transparent", // ✅ 100%達成の場合、赤枠を消す
                    "#b71c1c" // 🔴 それ以外は赤
                    // [
                    //   "interpolate",
                    //   ["linear"],
                    //   [
                    //     "/",
                    //     ["*", ["get", "untakenCount"], 100], // ✅ 未撮影数 × 100
                    //     ["+", ["get", "untakenCount"], ["get", "takenCount"]] // 総数 = 未撮影 + 撮影済み
                    //   ],
                    //   0, "#ffffff",  // 0% → 白 (全撮影済み)
                    //   10, "#ffcdd2", // 10% → ピンク薄め
                    //   20, "#ef9a9a", // 20% → やや濃いピンク
                    //   30, "#e57373", // 30% → ピンク
                    //   40, "#ef5350", // 40% → やや濃い赤
                    //   50, "#f44336", // 50% → 赤（ベース）
                    //   60, "#e53935", // 60% → さらに濃い赤
                    //   70, "#d32f2f", // 70% → 濃い赤
                    //   80, "#c62828", // 80% → かなり濃い赤
                    //   90, "#b71c1c"  // 90%以上 → 最も濃い赤
                    // ]
                  ],
                  "circle-stroke-opacity": [
                    "case",
                    [">", ["get", "untakenPercentage"], 0], 0.8, // 未撮影データがある場合、半透明
                    0
                  ]
                }}
              />

              {/* クラスター内のテキスト */}
              <Layer
                id="cluster-count-percentage"
                type="symbol"
                source="clustered-data"
                filter={["has", "point_count"]}
                layout={{
                  "text-field": [
                    "format",
                    ["to-string", ["get", "point_count"]], // 総数
                    { "font-scale": 1.1 },
                    "\n", {},
                    ["concat", "済 ", ["to-string", ["get", "takenCount"]], " (",
                    ["to-string", ["round", ["*", ["/", ["get", "takenCount"], ["get", "point_count"]], 100]]], "%)"],
                    { "font-scale": 0.8 },
                    "\n", {},
                    ["concat", "未 ", ["to-string", ["get", "untakenCount"]], " (",
                    ["to-string", ["round", ["*", ["/", ["get", "untakenCount"], ["get", "point_count"]], 100]]], "%)"],
                    { "font-scale": 0.8 },
                  ],
                  "text-size": [
                    "interpolate", ["linear"], ["zoom"],
                    3, 10,  // ズームレベル3で文字サイズ10px
                    6, 14,  // ズームレベル6で文字サイズ14px
                    10, 18, // ズームレベル10で文字サイズ18px
                    // 14, 22, // ズームレベル14で文字サイズ22px
                    // 18, 26  // ズームレベル18で文字サイズ26px
                  ],
                  "text-font": ["Open Sans Regular", "Arial Unicode MS Regular"],
                  "text-anchor": "center",
                  "text-optional": false,
                  "text-allow-overlap": true,
                }}
                paint={{
                  "text-color": "#000000", // 文字の色（黒）
                  "text-halo-color": "#ffffff", // 文字の背景を白に
                  "text-halo-width": 3 // 背景の厚みを調整
                }}
              />
            </>
          )}

          {status !== "both" && (
          <>
            <Layer
              id="clusters"
              type="circle"
              source="clustered-data"
              filter={["has", "point_count"]}
              paint={{
                "circle-color": "#3E3E3E",
                "circle-radius": [
                  "step",
                  ["get", "point_count"],
                  32, // デフォルトサイズ
                  100, 35,
                  750, 45
                ],
                "circle-opacity": 0.9, // 少し透過を減らす
              }}
            />

            <Layer
              id="cluster-count-percentage"
              type="symbol"
              source="clustered-data"
              filter={["has", "point_count"]}
              layout={{
                "text-field": [
                  "format",
                  ["get", "point_count"], // 点の数
                  { "font-scale": 1.5 },
                  "\n",
                  {},
                  ["case",
                    ["<", ["*", ["/", ["get", "point_count"], totalPoints], 100], 1], // 1%未満なら
                    "",
                    ["concat", // 1%以上なら通常の % 表示
                      ["to-string", [
                        "round",
                        ["*", ["/", ["get", "point_count"], totalPoints], 100] // 計算: (点の数 / 全体数) * 100
                      ]],
                      "%"
                    ]
                  ],
                  { "font-scale": 1.2 }
                ],
                "text-size": 16,
                "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
                "text-anchor": "center",
              }}
              paint={{
                "text-color": "#ffffff",
              }}
            />
          </>
          )}
          </>
        )}

          {/* クラスタを展開したときの個別ポイント表示 */}
          {showClusters && (
            <Layer
              id="unclustered-points"
              type="circle"
              source="clustered-data"
              filter={showClusters ? ["!", ["has", "point_count"]] : ["all"]}
              paint={{
                "circle-color": [
                  "match",
                  ["get", "status"],
                  "taken", "#4cb992",  //  `taken` は緑
                  "untaken", "#FF3B30",  // 　`untaken` は赤
                  "#808080" // それ以外は灰色
                ],
                "circle-radius": 6,
                "circle-stroke-width": 0,
                "circle-stroke-color": "#ffffff",
              }}
            />
          )}
        </Source>

        {!showClusters && (
          <Source
            id="unclustered-data"
            type="geojson"
            data={geojsonData}  // クラスターOFFのときはクラスタリングなし
            cluster={false}
            generateId={true}
          >
            <Layer
              id="unclustered-points-off"
              type="circle"
              source="unclustered-data"
              paint={{
                "circle-color": [
                  "match",
                  ["get", "status"],
                  "taken", "#4cb992",  //  `taken` は緑
                  "untaken", "#FF3B30",  // `untaken` は赤
                  "#808080" // それ以外は灰色
                ],
                "circle-radius": 6,
                "circle-stroke-width": 0,
                "circle-stroke-color": "#ffffff",
              }}
            />
          </Source>
        )}

        {/* ホバー時のツールチップ */}
        {hoveredFeature && (
          <Popup
            longitude={hoveredFeature.geometry.coordinates[0]}
            latitude={hoveredFeature.geometry.coordinates[1]}
            closeButton={false}
            closeOnClick={false}
            anchor="top"
            style={{
              backgroundColor: "white",
              padding: "0px",
              borderRadius: "8px",
              boxShadow: "0px 0px 10px rgba(0,0,0,0.2)",
            }}
            onMouseEnter={() => {
              popupHovered.current = true; // 🔥 Popupの上では `hoveredFeature` を変更しない
            }}
            onMouseLeave={() => {
              popupHovered.current = false;
              setTimeout(() => {
                if (!popupHovered.current) {
                  setHoveredFeature(null);
                }
              }, 300);
            }}
          >
            <div style={{ fontSize: "12px", fontWeight: "bold" }}>
              📍 {hoveredFeature.properties.prefectural_area}
            </div>
            <div>ID: {hoveredFeature.properties.id !== "null" ? hoveredFeature.properties.id : "--"}</div>
            <div>電柱番号: {hoveredFeature.properties.asset_number}</div>
            <div>支店コード: {hoveredFeature.properties.branch_code}</div>
            <div>市町村: {hoveredFeature.properties.city_name}</div>

            {/* Google Street View ボタン */}
            <button
              style={{
                marginTop: "10px",
                padding: "5px 10px",
                backgroundColor: "#4285F4",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                const lat = hoveredFeature.geometry.coordinates[1];
                const lng = hoveredFeature.geometry.coordinates[0];
                const googleStreetViewUrl = `https://www.google.com/maps?q=&layer=c&cbll=${lat},${lng}`;
                window.open(googleStreetViewUrl, "_blank");
              }}
              onMouseEnter={() => (popupHovered.current = true)} // ボタンの上でも消えない
              onMouseLeave={() => {
                popupHovered.current = false;
                setTimeout(() => {
                  if (!popupHovered.current) {
                    setHoveredFeature(null);
                  }
                }, 300);
              }}
            >
              🏠 Google Street View で見る
            </button>
          </Popup>
        )}

        <div style={{ position: "absolute", top: 10, left: 10, zIndex: 1 }}>
          <Chip
            size="small"
            onClick={toggleMapStyle}
            label={
              mapStyle.includes("yamasnax") ? "航空写真表示にする" : "デフォルト表示にする"
            }
            style={{ backgroundColor: "#4caf50", color: "#fff" }}
          />
          <Chip
            size="small"
            onClick={toggleTerrain}
            label={terrainEnabled ? "3D表示" : "2D表示"}
            style={{ backgroundColor: "#4caf50", color: "#fff" }}
          />
          <Chip
            size="small"
            onClick={() => setShowClusters(!showClusters)}
            label={showClusters ? "クラスター表示" : "クラスター非表示"}
            style={{ backgroundColor: showClusters ? "#4caf50" : "#7f898b", color: "#fff" }}
          />
        </div>

        {terrainEnabled && (
          <>
            <Source
              id="mapbox-dem"
              type="raster-dem"
              url="mapbox://mapbox.mapbox-terrain-dem-v1"
              tileSize={512}
              maxzoom={14}
            />
            <Layer
              id="terrain-data"
              type="hillshade"
              source="mapbox-dem"
              paint={{
                "hillshade-exaggeration": 0.5,
              }}
            />
          </>
        )}
      </ReactMapGL>
    </div>
  );
}

export default PhotoShootMapHokuden;
